import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Volume2, VolumeX, Maximize, Minimize } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { formatCurrency } from '@/lib/utils'

const GAME_NAMES: Record<string, string> = {
  ruleta: 'Ruleta',
  blackjack: 'Blackjack',
  baccarat: 'Baccarat',
  'blackjack-multiplayer': 'Blackjack Multiplayer',
}

export default function GameLayout() {
  const navigate = useNavigate()
  const { gameId } = useParams()
  const { user, isDemo } = useAuth()
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  if (!user) {
    return null
  }

  const gameName = GAME_NAMES[gameId || ''] || 'Juego'

  return (
    <div className="min-h-screen bg-casino-gradient flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-casino-black/90 backdrop-blur-sm border-b border-casino-gold/20">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          {/* Back button and game name */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/lobby')}
              className="text-casino-gold hover:text-casino-gold-light"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-cinzel text-casino-gold font-bold text-lg">
              {gameName}
            </h1>
          </div>

          {/* Balance and controls */}
          <div className="flex items-center gap-4">
            {/* Balance */}
            <div className="text-right">
              <p className="text-xs text-gray-400">
                {isDemo ? 'Demo' : 'Balance'}
              </p>
              <p className="font-bold text-casino-gold">
                {formatCurrency(user.balance)}
              </p>
            </div>

            {/* Game controls */}
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMuted(!isMuted)}
                className="text-gray-400 hover:text-white"
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                className="text-gray-400 hover:text-white"
              >
                {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Game content */}
      <main className="flex-1 flex flex-col">
        <Outlet context={{ isMuted }} />
      </main>
    </div>
  )
}
